import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Wrench, Clock, Users, ArrowRight } from 'lucide-react'
import useKibuClubPopupStore from '@/context/KibuClubPopupStore'

const KibuClubPopup: React.FC = () => {
    const { isOpen, dismissPopup } = useKibuClubPopupStore()

    if (!isOpen) return null

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                onClick={dismissPopup}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <button
                        onClick={dismissPopup}
                        className="absolute top-4 right-4 z-50 p-2 text-gray-400 hover:text-gray-600 transition-colors bg-white/80 rounded-full shadow-lg hover:bg-white"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    {/* Yellow accent bar */}
                    <div className="h-2 bg-specialYellow" />

                    <div className="p-8 md:p-12">
                        {/* Logo */}
                        <div className="flex justify-center mb-6">
                            <svg className="h-8 w-auto" viewBox="0 0 3276 669" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3149.28 518.576C3111.47 518.576 3079.68 503.78 3063.24 479.12H3059.95L3056.67 512H3001.87V117.44H3064.34V265.948H3067.63C3084.61 243.48 3114.21 230.328 3149.83 230.328C3222.16 230.328 3275.87 291.704 3275.87 374.452C3275.87 457.2 3222.16 518.576 3149.28 518.576ZM3064.34 374.452C3064.34 427.608 3092.83 462.132 3136.67 462.132C3180.51 462.132 3210.65 426.512 3210.65 374.452C3210.65 322.392 3180.51 286.772 3136.67 286.772C3092.83 286.772 3064.34 321.844 3064.34 374.452Z"
                                    fill="#FFB511" />
                                <path d="M2792.37 518.576C2732.64 518.576 2688.8 475.832 2688.8 412.264V236.904H2751.27V400.208C2751.27 438.568 2772.64 461.584 2806.07 461.584C2841.69 461.584 2864.71 435.28 2864.71 395.824V236.904H2927.18V512H2872.38L2869.64 482.408H2866.35C2849.36 505.424 2825.8 518.576 2792.37 518.576Z"
                                    fill="#FFB511" />
                                <path d="M2552.33 512V117.44H2614.81V512H2552.33Z"
                                    fill="#FFB511" />
                                <path d="M2323.84 518.576C2212.05 518.576 2135.33 435.28 2135.33 320.748C2135.33 207.312 2213.7 121.824 2323.84 121.824C2410.43 121.824 2481.12 174.432 2494.27 251.152H2426.87C2411.52 205.668 2373.16 181.556 2323.84 181.556C2249.86 181.556 2202.19 235.808 2202.19 320.2C2202.19 399.112 2246.58 458.844 2323.84 458.844C2372.62 458.844 2412.62 435.828 2427.42 389.248H2494.82C2482.22 467.064 2411.52 518.576 2323.84 518.576Z"
                                    fill="#FFB511" />
                                <path d="M560.421 345.115L619.962 285.574C650.268 255.313 669 213.478 669 167.272C669 74.9057 594.115 0.0222998 501.75 0.0222998C455.544 0.0222998 413.71 18.7543 383.448 49.06L334.522 97.9862V0H0V334.5H97.9639L48.9262 383.538C18.7097 413.799 0 455.589 0 501.75C0 594.115 74.8834 669 167.25 669C213.456 669 255.29 650.268 285.552 619.962L345.092 560.421C368.864 623.84 430.011 669 501.728 669C594.094 669 668.978 594.115 668.978 501.75C668.978 430.033 623.818 368.864 560.399 345.115H560.421Z"
                                    fill="#FFB511" />
                                <path d="M1847.7 520.356C1788.34 520.356 1744.76 477.871 1744.76 414.689V240.392H1806.85V402.706C1806.85 440.833 1828.1 463.71 1861.32 463.71C1896.73 463.71 1919.6 437.565 1919.6 398.349V240.392H1981.69V513.82H1927.23L1924.5 484.407H1921.24C1904.35 507.285 1880.93 520.356 1847.7 520.356Z"
                                    fill="#FFB511" />
                                <path d="M1557.77 520.34C1520.19 520.34 1488.6 505.634 1472.25 481.123H1468.99L1465.72 513.804H1411.25V121.636H1473.34V269.244H1476.61C1493.5 246.912 1522.91 233.84 1558.31 233.84C1630.21 233.84 1683.59 294.843 1683.59 377.089C1683.59 459.336 1630.21 520.34 1557.77 520.34ZM1545.24 464.237C1588.82 464.237 1618.77 428.833 1618.77 377.089C1618.77 325.345 1588.82 289.941 1545.24 289.941C1501.67 289.941 1473.34 324.802 1473.34 377.089C1473.34 429.924 1501.67 464.237 1545.24 464.237Z"
                                    fill="#FFB511" />
                                <path d="M1275.65 513.802V240.376H1337.74V513.802H1275.65ZM1306.7 201.159C1284.91 201.159 1267.48 184.274 1267.48 161.942C1267.48 139.61 1284.91 122.725 1306.7 122.725C1328.48 122.725 1345.91 139.61 1345.91 161.942C1345.91 184.274 1328.48 201.159 1306.7 201.159Z"
                                    fill="#FFB511" />
                                <path d="M937.574 513.808V132.535H1001.85V288.858H1029.62L1141.83 132.535H1218.08L1086.27 316.09V319.358L1226.25 513.808H1150L1029.62 347.138H1001.85V513.808H937.574Z"
                                    fill="#FFB511" />
                            </svg>
                        </div>

                        {/* Badge */}
                        <div className="flex justify-center mb-4">
                            <div className="inline-flex items-center gap-2 bg-specialYellow/20 backdrop-blur-sm text-black px-4 py-2 rounded-full text-sm font-headline">
                                <span className="w-2 h-2 bg-specialYellow rounded-full animate-pulse"></span>
                                NOW IN HACKNEY
                            </div>
                        </div>

                        {/* Title */}
                        <div className="text-center mb-4 md:mb-6">
                            <h2 className="text-2xl md:text-4xl font-headline font-bold text-gray-900">
                                Build Your Own Headphones
                            </h2>
                            <p className="text-base md:text-lg text-gray-700 font-medium hidden md:block mt-3">
                                Hands-on workshop for kids aged 5+
                            </p>
                        </div>

                        {/* Features */}
                        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
                            <div className="flex flex-col items-center text-center p-2 md:p-4 bg-gray-50 rounded-lg">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-specialYellow rounded-full flex items-center justify-center mb-2 md:mb-3">
                                    <Wrench className="w-5 h-5 md:w-6 md:h-6 text-black" />
                                </div>
                                <p className="text-xs md:text-sm font-medium text-gray-900">Build from scratch</p>
                                <p className="text-[10px] md:text-xs text-gray-600 mt-0.5 md:mt-1 hidden md:block">Learn 3D printing & repair skills</p>
                            </div>
                            <div className="flex flex-col items-center text-center p-2 md:p-4 bg-gray-50 rounded-lg">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-specialYellow rounded-full flex items-center justify-center mb-2 md:mb-3">
                                    <Clock className="w-5 h-5 md:w-6 md:h-6 text-black" />
                                </div>
                                <p className="text-xs md:text-sm font-medium text-gray-900">75 minutes</p>
                                <p className="text-[10px] md:text-xs text-gray-600 mt-0.5 md:mt-1 hidden md:block">Complete build session</p>
                            </div>
                            <div className="flex flex-col items-center text-center p-2 md:p-4 bg-gray-50 rounded-lg">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-specialYellow rounded-full flex items-center justify-center mb-2 md:mb-3">
                                    <Users className="w-5 h-5 md:w-6 md:h-6 text-black" />
                                </div>
                                <p className="text-xs md:text-sm font-medium text-gray-900">Expert guidance</p>
                                <p className="text-[10px] md:text-xs text-gray-600 mt-0.5 md:mt-1 hidden md:block">Step-by-step support</p>
                            </div>
                        </div>

                        {/* Description */}
                        <p className="text-gray-600 text-center mb-8 leading-relaxed">
                            In this hands-on workshop, kids discover how 3D printing works, learn basic repair skills,
                            and understand why fixing things matters for sustainability. They assemble their own working
                            headphones and take them home to keep!
                        </p>

                        {/* CTA Button */}
                        <div className="flex justify-center">
                            <a
                                href="https://kibu.club"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-specialYellow text-black rounded-lg font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                Learn More
                                <ArrowRight className="w-5 h-5" />
                            </a>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}

export default KibuClubPopup
